import React, { useEffect, useState } from 'react';
import { appointmentService } from '../../services/appointment.service';
import { WorkingHoursDto } from '../../types/appointment.types';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  CircularProgress,
  Alert,
  Switch,
  TextField,
} from '@mui/material';
import { toastService } from '../../services/toast.service';

const daysOfWeek = ['Понеділок', 'Вівторок', 'Середа', 'Четвер', 'П\'ятниця', 'Субота', 'Неділя'];

export const ScheduleSettings: React.FC = () => {
  const [schedule, setSchedule] = useState<WorkingHoursDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSchedule = async () => {
      try {
        setLoading(true);
        const data = await appointmentService.getExpertSchedule();
        // Ensure we have a full 7-day schedule
        const fullSchedule = daysOfWeek.map((_, index) => {
          const dayIndex = index + 1;
          const existingDay = data.find(d => d.dayOfWeek === dayIndex);
          return existingDay || { dayOfWeek: dayIndex, startTime: '09:00', endTime: '18:00', isActive: false };
        });
        setSchedule(fullSchedule);
      } catch (err) {
        setError('Не вдалося завантажити графік.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchSchedule();
  }, []);

  const handleScheduleChange = (dayOfWeek: number, field: keyof WorkingHoursDto, value: any) => {
    setSchedule(currentSchedule =>
      currentSchedule.map(day =>
        day.dayOfWeek === dayOfWeek ? { ...day, [field]: value } : day
      )
    );
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await appointmentService.updateExpertSchedule({ workingHours: schedule });
      toastService.success('Графік успішно збережено');
    } catch (err) {
      toastService.error('Не вдалося зберегти графік.');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <Paper sx={{ p: 3, mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        Графік роботи
      </Typography>
      {error && <Alert severity="error">{error}</Alert>}
      <Box component="form" noValidate autoComplete="off">
        {schedule.map((day, index) => (
          <Grid container spacing={2} alignItems="center" key={day.dayOfWeek} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={2}>
              <Typography>{daysOfWeek[index]}</Typography>
            </Grid>
            <Grid item xs={6} sm={2}>
              <Switch
                checked={day.isActive}
                onChange={(e) => handleScheduleChange(day.dayOfWeek, 'isActive', e.target.checked)}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Час початку"
                type="time"
                value={day.startTime}
                onChange={(e) => handleScheduleChange(day.dayOfWeek, 'startTime', e.target.value)}
                disabled={!day.isActive}
                fullWidth
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Час закінчення"
                type="time"
                value={day.endTime}
                onChange={(e) => handleScheduleChange(day.dayOfWeek, 'endTime', e.target.value)}
                disabled={!day.isActive}
                fullWidth
              />
            </Grid>
          </Grid>
        ))}
      </Box>
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" onClick={handleSave} disabled={isSaving}>
          {isSaving ? <CircularProgress size={24} /> : 'Зберегти графік'}
        </Button>
      </Box>
    </Paper>
  );
};
